import { z } from "zod";
import { NOTIFICATION_PREFERENCE_CATEGORIES } from "./notification-config.js";

/**
 * Shared DTO cho Notification Center — slice READ in-app (US-E3.F3-0017 / @UC0008).
 *
 * Một nguồn sự thật cho api (S01 — list + mark-read) và mobile (S02 — render),
 * tránh drift shape (DRY). C5: validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN Phase 8.5: đây CHỈ là phần đọc/đánh-dấu in-app notifications
 * (GET /v1/notifications + PATCH read-all + PATCH :id/read) — KHÔNG dispatcher/worker/preferences.
 * Plan bất biến: `notifications.title/body` là snapshot đã render; channel `in_app` luôn lưu.
 *
 * BẢO MẬT (invariants):
 * - C2/C3: item CHỈ field cần cho UI; `.strict()` chặn userId/secret/field lạ lọt ra response.
 * - PII: notification của CHÍNH user (server lọc theo token); contract không mang field cross-user.
 * - Cursor opaque string — đối xứng common/pagination/cursor (envelope {data, pageInfo}).
 */

/** Max page-size. Default (20) do backend áp khi vắng. */
export const NOTIFICATIONS_MAX_LIMIT = 50;

/** Độ dài tối đa của cursor opaque tại biên (chặn input phình to). */
export const NOTIFICATIONS_MAX_QUERY_LENGTH = 512;

/**
 * Một notification in-app (UC0008). title/body/data NULLABLE (snapshot có thể trống).
 * readAt = ISO string khi đã đọc, null khi chưa đọc. `.strict()` chặn userId/secret.
 */
export const notificationItemSchema = z
  .object({
    id: z.string().uuid(),
    type: z.string(),
    title: z.string().nullable(),
    body: z.string().nullable(),
    // ISO-8601 string khi serialize JSON.
    createdAt: z.string(),
    // null = chưa đọc; ISO string = đã đọc.
    readAt: z.string().nullable(),
    // Payload điều hướng (vd { groupId }) — nullable; KHÔNG chứa PII thô / secret.
    data: z.unknown().nullable(),
    // US-E23.F4-0334-S09 (@UC9519): null = chưa lưu trữ. KHÔNG có `doneAt` — "việc đã xong" là câu
    // hỏi về THẾ GIỚI, dẫn xuất qua `GET /me/action-items`, không phải cờ đóng băng ở đây.
    archivedAt: z.string().nullable(),
  })
  .strict();

export type NotificationItem = z.infer<typeof notificationItemSchema>;

/**
 * US-E23.F4-0334-S07 (@UC9518) — trạng thái lọc hộp thư. Ba tab Figma map lên đây.
 *
 * 🔴 `archived` TÁCH HẲN khỏi `unread|read`: đã lưu trữ thì KHÔNG xuất hiện ở hai tab kia nữa
 * (AC1), nên đây là ba tập RỜI NHAU chứ không phải ba bộ lọc chồng lên nhau.
 */
export const NOTIFICATION_STATUS_FILTER = {
  UNREAD: "unread",
  READ: "read",
  ARCHIVED: "archived",
} as const;
export type NotificationStatusFilter =
  (typeof NOTIFICATION_STATUS_FILTER)[keyof typeof NOTIFICATION_STATUS_FILTER];
export const notificationStatusFilterSchema = z.enum([
  NOTIFICATION_STATUS_FILTER.UNREAD,
  NOTIFICATION_STATUS_FILTER.READ,
  NOTIFICATION_STATUS_FILTER.ARCHIVED,
]);

/**
 * 🔴 TÁI DÙNG BỘ 5 CATEGORY CỦA `NotificationPreference` — KHÔNG đẻ bộ phân loại thứ hai.
 *
 * Figma có 3 chip (`Hệ thống` · `Đóng quỹ` · `Lời nhắc`); chúng là **NHÃN GỘP** ở tầng hiển thị,
 * map lên bộ 5 này. Nếu khai một enum 3 giá trị riêng cho API thì hệ thống có hai danh sách không
 * ràng buộc nhau — đúng cái bẫy đã trả giá nhiều lần trong repo (drift 2 whitelist).
 */
export const notificationCategoryFilterSchema = z.enum(NOTIFICATION_PREFERENCE_CATEGORIES);

/** Query list: cursor + limit + (S07) lọc theo category/status. */
export const notificationsListQuerySchema = z.object({
  cursor: z.string().max(NOTIFICATIONS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(NOTIFICATIONS_MAX_LIMIT).optional(),
  category: notificationCategoryFilterSchema.optional(),
  status: notificationStatusFilterSchema.optional(),
});

export type NotificationsListQuery = z.infer<typeof notificationsListQuerySchema>;

/**
 * Envelope list khớp quy ước repo `{ data, pageInfo }` + `unreadCount` cho bell badge.
 * unreadCount = số notification chưa đọc của user (≥0) — nguồn cho badge biến mất sau read-all.
 */
export const notificationsListResponseSchema = z.object({
  data: z.array(notificationItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
  unreadCount: z.number().int().min(0),
});

export type NotificationsListResponse = z.infer<typeof notificationsListResponseSchema>;

/** Response PATCH /:id/read — trả item sau khi đánh dấu đã đọc (AC3). */
export const markReadResponseSchema = notificationItemSchema;

export type MarkReadResponse = z.infer<typeof markReadResponseSchema>;

/** Response PATCH /read-all — số notification được cập nhật (AC4, idempotent). */
export const markAllReadResponseSchema = z.object({
  updatedCount: z.number().int().min(0),
});

export type MarkAllReadResponse = z.infer<typeof markAllReadResponseSchema>;
